import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Skeleton,
  Alert,
  Pagination,
  Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import EstimateFilter from './EstimateFilter';
import ItemSearchBar from './ItemSearchBar';
import { getCalibrationItems, getCalibrationSummary } from '../../utils/analyticsApi';

const ITEMS_PER_PAGE = 12;

const CalibrationGallery = ({ scope, organizationId, teamId }) => {
  const [summary, setSummary] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [error, setError] = useState(null);

  // Fetch summary on mount or when workspace filter changes
  useEffect(() => {
    fetchSummary();
    // Reset selection when workspace changes
    setSelectedEstimate(null);
    setItems([]);
    setTotalItems(0);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, organizationId, teamId]);

  // Fetch items when filter changes
  useEffect(() => {
    if (selectedEstimate) {
      fetchItems();
    } else {
      setItems([]);
      setTotalItems(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEstimate, searchTerm, page, scope, organizationId, teamId]);

  const fetchSummary = async () => {
    setLoadingSummary(true);
    setError(null);
    try {
      const response = await getCalibrationSummary({ organizationId, teamId });
      setSummary(response.data.summary || []);
    } catch (err) {
      console.error('Error fetching summary:', err);
      setError('Failed to load calibration summary');
    } finally {
      setLoadingSummary(false);
    }
  };

  const fetchItems = async () => {
    if (!selectedEstimate) return;

    setLoadingItems(true);
    setError(null);
    try {
      const response = await getCalibrationItems(selectedEstimate, {
        search: searchTerm,
        limit: ITEMS_PER_PAGE,
        offset: (page - 1) * ITEMS_PER_PAGE,
        organizationId,
        teamId
      });
      setItems(response.data.items || []);
      setTotalItems(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching items:', err);
      setError('Failed to load items');
    } finally {
      setLoadingItems(false);
    }
  };

  const handleEstimateSelect = (estimate) => {
    setSelectedEstimate(estimate);
    setPage(1);
    setSearchTerm('');
  };

  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
    setPage(1);
  }, []);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getConsensusIcon = (rate) => {
    if (rate >= 80) {
      return <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />;
    } else if (rate >= 60) {
      return <WarningIcon fontSize="small" sx={{ color: 'warning.main' }} />;
    }
    return <ErrorIcon fontSize="small" sx={{ color: 'error.main' }} />;
  };

  const getConsensusColor = (rate) => {
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'warning';
    return 'error';
  };

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Calibration Gallery
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Browse past estimations by point value to maintain consistency across sessions
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <EstimateFilter
        summary={summary}
        selectedEstimate={selectedEstimate}
        onSelectEstimate={handleEstimateSelect}
        loading={loadingSummary}
      />

      {selectedEstimate && (
        <ItemSearchBar
          onSearch={handleSearch}
          placeholder={`Search items with estimate "${selectedEstimate}"...`}
        />
      )}

      {loadingItems ? (
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="80%" height={28} />
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="40%" height={20} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : items.length > 0 ? (
        <>
          <Grid container spacing={2}>
            {items.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'box-shadow 0.2s',
                    '&:hover': {
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          flex: 1,
                          mr: 1
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Chip
                        label={item.finalEstimate}
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>

                    {item.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {item.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {item.jiraKey && (
                        <Chip
                          label={item.jiraKey}
                          size="small"
                          variant="outlined"
                          component={item.jiraUrl ? 'a' : 'span'}
                          href={item.jiraUrl || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          clickable={!!item.jiraUrl}
                          sx={{ height: 22, fontSize: '0.75rem' }}
                        />
                      )}
                      <Tooltip title={`${item.consensusRate}% consensus`}>
                        <Chip
                          icon={getConsensusIcon(item.consensusRate)}
                          label={`${item.consensusRate}%`}
                          size="small"
                          color={getConsensusColor(item.consensusRate)}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.75rem' }}
                        />
                      </Tooltip>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                      <Typography variant="caption" color="text.secondary">
                        {item.sessionName}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {formatDate(item.estimatedAt)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
            Showing {items.length} of {totalItems} items
          </Typography>
        </>
      ) : selectedEstimate ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No items found with estimate "{selectedEstimate}"
            {searchTerm && ` matching "${searchTerm}"`}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Select an estimate value above to view items
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CalibrationGallery;
